import {
    action,
    configure,
    decorate,
    observable,
    runInAction,
} from "mobx";

configure({ enforceActions: "always" });

const audioPath = "assets/sounds/sfx/";
const announcementPath = "assets/sounds/announcements/";
const countdownFile = "slow_clock_ticking.mp3";

class VirtualAuctionStore {
    constructor() {
        this.initialize();
    }

    initialize = () =>
        runInAction(() => {
            this.audioLane = null;
            this.activeLaneID = null;
            this.voice = "kendall";
            this.countdownKey = 1;
            this.isCountdownActive = false;
            this.isBonusTimeActive = false;
            this.audioContext = new (window.AudioContext ||
                window.webkitAudioContext)();

            // --- NEW: Create audio channels ---
            this.sfxGain = this.audioContext.createGain();
            this.voiceGain = this.audioContext.createGain();
            this.sfxGain.connect(this.audioContext.destination);
            this.voiceGain.connect(this.audioContext.destination);

            this.virtualLaneList = [];
            this.auction = {};
            this.bidStatusCode = null;
            this.bidStatusMessage = null;

            this.sfxEnabled = true;
            this.voiceEnabled = true;

            const savedPrefs = JSON.parse(localStorage.getItem('va_audio_prefs') || '{}');
            const defaultPrefs = {
                countdownClock: true, yourBid: true, externalBid: true,
                outbidAlert: true, pauseMusic: true, newMessage: true,
                voiceNewItem: true, voiceOutbid: true, voiceTimeExtended: true, voiceOutcome: true,
            };
            this.soundPrefs = { ...defaultPrefs, ...savedPrefs };

            // Keep references to active sources
            this.effectSource = null;
            this.voiceSource = null;
            this.loopingSource = null;

            this.effectPlayId = 0;
            this.voicePlayId = 0;
            this.loopingPlayId = 0;
            this.currentLoopingFile = null;
            this.bonusTimeGeneration = 0;
            this.isPaused = false;
            this.announcementSource = null;
            this.announcementPlayId = 0;
            this.isAnnouncementPlaying = false;
            this.announcementDuration = 0;
        });

    // --- Utility: load and play audio into a channel ---
    _loadAndPlay = async (filePath, gainNode, loop = false) => {
        try {
            const response = await fetch(filePath);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(
                arrayBuffer
            );

            const source = this.audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.loop = loop;

            source.connect(gainNode);
            source.start(0);

            return source; // Keep reference if you need to stop it later
        } catch (err) {
            console.error("Error loading audio:", filePath, err);
            return null;
        }
    };

    // --- SFX ---
    playEffect = async (soundFile) => {
        if (!this.isAudioEnabled()) return;
        const src = `${audioPath}${soundFile}`;
        const myId = ++this.effectPlayId;

        if (this.effectSource) {
            try { this.effectSource.stop(); this.effectSource.disconnect(); } catch {}
            this.effectSource = null;
        }

        const s = await this._loadAndPlay(src, this.sfxGain, false);
        if (!s || myId !== this.effectPlayId) {
            try { s.stop(); s.disconnect(); } catch {}
            return;
        }
        this.effectSource = s;
    };


    // --- VoiceOver ---
    playVoiceOver = async (soundFile, voice = null) => {
        if (!this.isAudioEnabled()) return;
        const src = `${audioPath}voices/${voice ? voice : this.voice}/${soundFile}`;
        const myId = ++this.voicePlayId;

        if (this.voiceSource) {
            try { this.voiceSource.stop(); this.voiceSource.disconnect(); } catch {}
            this.voiceSource = null;
        }

        const s = await this._loadAndPlay(src, this.voiceGain, false);
        if (!s || myId !== this.voicePlayId) {
            try { s.stop(); s.disconnect(); } catch {}
            return;
        }
        this.voiceSource = s;
    };


    // --- Pre-auction announcement ---
    playAnnouncement = async (soundFile) => {
        if (!this.isAudioEnabled()) return;
        if (this.isAnnouncementPlaying) return;

        const src = `${announcementPath}${soundFile}`;
        const myId = ++this.announcementPlayId;

        if (this.announcementSource) {
            try { this.announcementSource.stop(); this.announcementSource.disconnect(); } catch {}
            this.announcementSource = null;
        }

        const s = await this._loadAndPlay(src, this.voiceGain, false);
        console.log('[Announcements] _loadAndPlay result:', s, '| myId:', myId, '| announcementPlayId:', this.announcementPlayId);
        if (!s || myId !== this.announcementPlayId) {
            if (s) { try { s.stop(); s.disconnect(); } catch {} }
            return;
        }

        runInAction(() => {
            this.isAnnouncementPlaying = true;
            this.announcementDuration = s.buffer?.duration ?? 0;
        });
        console.log('[Announcements] isAnnouncementPlaying set to true');
        this.announcementSource = s;
        s.onended = () => {
            runInAction(() => {
                this.isAnnouncementPlaying = false;
                this.announcementSource = null;
            });
        };
    };

    stopAnnouncement = () => {
        this.announcementPlayId++;
        if (this.announcementSource) {
            try { this.announcementSource.stop(); this.announcementSource.disconnect(); } catch {}
        }
        runInAction(() => {
            this.announcementSource = null;
            this.isAnnouncementPlaying = false;
            this.announcementDuration = 0;
        });
    };

    // --- Looping countdown sound ---
    startLoopingSound = async (soundFile) => {
        if (!this.isAudioEnabled()) return;

        const src = `${audioPath}${soundFile}`;

        // No-op if same file already looping
        if (this.currentLoopingFile === src && this.loopingSource) return;

        const myId = ++this.loopingPlayId;

        // Stop current immediately to prevent overlap
        if (this.loopingSource) {
            try { this.loopingSource.stop(); this.loopingSource.disconnect(); } catch {}
            this.loopingSource = null;
        }

        const newSource = await this._loadAndPlay(src, this.sfxGain, true);
        if (!newSource) return;

        // If a newer request arrived while loading, discard this one
        if (myId !== this.loopingPlayId) {
            try { newSource.stop(); newSource.disconnect(); } catch {}
            return;
        }

        this.loopingSource = newSource;
        this.currentLoopingFile = src;
    };


    stopLoopingSound = () => {
        // Invalidate any in-flight start requests
        this.loopingPlayId++;
        if (this.loopingSource) {
            try { this.loopingSource.stop(); this.loopingSource.disconnect(); } catch {}
        }
        this.loopingSource = null;
        this.currentLoopingFile = null;
    };

    // --- Countdown logic ---
    resetCountdown = (duration) => {
        this.stopCountdown();
        this.startCountdown(duration);
        runInAction(() => {
            this.countdownKey = this.countdownKey + 1;
        });
    };

    restartCurrentAudio = () => {
        if (this.isPaused) {
            if (this.soundPrefs.pauseMusic) {
                this.startLoopingSound("music/NPAPauseAuction.mp3");
            }
            return;
        }
        if (!this.isCountdownActive) return;
        if (!this.soundPrefs.countdownClock) return;
        const timeRemaining = this.getRemainingTime();
        if (timeRemaining <= 10) {
            this.startLoopingSound("clock_ticking.mp3");
        } else {
            this.startLoopingSound(countdownFile);
        }
    };

    getRemainingTime = () => {
        if (!this.countdownStartTime || !this.duration) return 0;
        const elapsed = (Date.now() - this.countdownStartTime) / 1000;
        return Math.max(0, this.duration - elapsed);
    };

    startCountdown = (duration) => {
        runInAction(() => {
            this.isCountdownActive = true;
            this.isBonusTimeActive = false;
            this.duration = duration;
            this.countdownStartTime = Date.now();
        });
        this.startTimer(duration);

        if (this.isAudioEnabled()) {
            if (this.soundPrefs.countdownClock) {
                this.startLoopingSound(countdownFile);
            }

            if (this.fastTickingTimer) clearTimeout(this.fastTickingTimer);

            if (duration > 5) {
                this.fastTickingTimer = setTimeout(() => {
                    if (this.isCountdownActive && this.isAudioEnabled() && this.soundPrefs.countdownClock) {
                        this.startLoopingSound("clock_ticking.mp3");
                    }
                }, (duration - 5) * 1000);
            } else {
                if (this.soundPrefs.countdownClock) {
                    this.startLoopingSound("clock_ticking.mp3");
                }
            }
        } else {
            this.stopLoopingSound();
        }
    };

    startBonusTime = async (duration) => {
        runInAction(() => {
            this.isBonusTimeActive = true;
            this.isCountdownActive = true;
            this.duration = duration;
            this.countdownStartTime = Date.now();
        });
        this.startTimer(duration);

        if (this.isAudioEnabled()) {
            const myGeneration = ++this.bonusTimeGeneration;
            if (this.soundPrefs.voiceTimeExtended) {
                await this.playVoiceOver("time_extended.mp3");
            }

            setTimeout(() => {
                if (myGeneration !== this.bonusTimeGeneration) return;
                if (!this.isCountdownActive || !this.isAudioEnabled()) return;

                const remainingAfterVoice = duration - 1.5;

                if (this.soundPrefs.countdownClock) {
                    this.startLoopingSound(countdownFile);
                }

                if (this.fastTickingTimer) clearTimeout(this.fastTickingTimer);

                if (remainingAfterVoice > 5) {
                    this.fastTickingTimer = setTimeout(() => {
                        if (this.isCountdownActive && this.isAudioEnabled() && !this.isPaused && this.soundPrefs.countdownClock) {
                            this.startLoopingSound("clock_ticking.mp3");
                        }
                    }, (remainingAfterVoice - 5) * 1000);
                } else {
                    if (this.soundPrefs.countdownClock) {
                        this.startLoopingSound("clock_ticking.mp3");
                    }
                }
            }, 1500);
        } else {
            this.stopLoopingSound();
        }
    };

    stopCountdown = () => {
        runInAction(() => {
            this.isCountdownActive = false;
            this.isBonusTimeActive = false;
            this.duration = 0;
            this.countdownStartTime = null;
        });

        if (this.countdownTimer) clearTimeout(this.countdownTimer);
        if (this.fastTickingTimer) clearTimeout(this.fastTickingTimer);

        this.countdownTimer = null;
        this.fastTickingTimer = null;
        this.bonusTimeGeneration++;

        this.stopLoopingSound();
    };

    startTimer = (duration) => {
        if (this.countdownTimer) {
            clearTimeout(this.countdownTimer);
        }
        this.countdownTimer = setTimeout(() => {
            if (this.isCountdownActive && !this.isPaused) {
                runInAction(() => {
                    this.stopCountdown();
                });
            }
        }, duration * 1000);
    };

    // --- Pause audio ---
    setLanePaused = (paused) => {
        runInAction(() => { this.isPaused = paused; });

        if (paused) {
            // Cancel pending timers so they can't overwrite the pause audio
            if (this.fastTickingTimer) { clearTimeout(this.fastTickingTimer); this.fastTickingTimer = null; }
            if (this.countdownTimer) { clearTimeout(this.countdownTimer); this.countdownTimer = null; }

            if (this.isAudioEnabled()) {
                this.stopLoopingSound();
                if (this.soundPrefs.pauseMusic) {
                    this.startLoopingSound("music/NPAPauseAuction.mp3");
                }
            }
        } else {
            this.stopLoopingSound();
            this.restartCurrentAudio();
        }
    };

    // --- Lane / Auction state ---
    setCurrentLaneAudio = (activeLaneID, value) => {
        const isVirtualLane = this.virtualLaneList.some(
            (lane) => lane.laneID === activeLaneID
        );

        runInAction(() => {
            if (!this.auction[activeLaneID]) {
                this.auction[activeLaneID] = {};
            }
            this.auction[activeLaneID].audioPlaying = value;
            this.auction[activeLaneID].isVirtual = isVirtualLane;
            this.activeLaneID = activeLaneID;
        });

        if (isVirtualLane && !value) {
            this.stopLoopingSound();
        } else if (isVirtualLane && value) {
            if (this.isCountdownActive || this.isPaused) {
                this.restartCurrentAudio();
            }
        }
    };

    isAudioEnabled = () => {
        if (this.activeLaneID) {
            const lane = this.auction[this.activeLaneID];
            return lane && lane.audioPlaying && lane.isVirtual;
        }
        return false;
    };

    // --- Mute toggles ---
    setSfxEnabled = (enabled) => {
        runInAction(() => {
            this.sfxEnabled = enabled;
            this.sfxGain.gain.value = enabled ? 1 : 0;
        });
    };

    setVoiceEnabled = (enabled) => {
        runInAction(() => {
            this.voiceEnabled = enabled;
            this.voiceGain.gain.value = enabled ? 1 : 0;
        });
    };

    setSoundPref = (key, value) => {
        runInAction(() => { this.soundPrefs = { ...this.soundPrefs, [key]: value }; });
        localStorage.setItem('va_audio_prefs', JSON.stringify(this.soundPrefs));
    };

    // --- Misc ---
    setVoiceOver = (name) => {
        runInAction(() => {
            this.voice = name;
        });
    };

    setProperty = (key, value) => {
        runInAction(() => {
            this[key] = value;
        });
    };

    resetBidStatus = () => {
        this.setProperty("bidStatusCode", null);
        this.setProperty("bidStatusMessage", null);
    };

    resetBonusTime = async (duration) => {
        this.stopCountdown();
        await this.startBonusTime(duration);
        runInAction(() => {
            this.countdownKey = this.countdownKey + 1;
        });
    };
}

decorate(VirtualAuctionStore, {
    resetCountdown: action,
    resetBonusTime: action,
    resetBidStatus: action,
    startLoopingSound: action,
    stopLoopingSound: action,
    startCountdown: action,
    startBonusTime: action,
    stopCountdown: action,
    isCountdownActive: observable,
    isBonusTimeActive: observable,
    duration: observable,
    countdownKey: observable,
    voice: observable,
    playEffect: action,
    playVoiceOver: action,
    playAnnouncement: action,
    stopAnnouncement: action,
    isAnnouncementPlaying: observable,
    announcementDuration: observable,
    setVoiceOver: action,
    setCurrentLaneAudio: action,
    setLanePaused: action,
    setProperty: action,
    restartCurrentAudio: action,
    isPaused: observable,
    auction: observable,
    activeLaneID: observable,
    virtualLaneList: observable,
    bidStatusCode: observable,
    bidStatusMessage: observable,
    currentLoopingSoundFile: observable,
    sfxEnabled: observable,
    voiceEnabled: observable,
    soundPrefs: observable,
    setSoundPref: action,
});

export default VirtualAuctionStore;
